const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com'

export interface BourbonListing {
  id: string
  type: 'have' | 'want'
  userId: string
  userName: string
  userMobile?: string
  title: string
  brand: string
  age?: string | number // Support both string (e.g. "NAS") and number (e.g. 12)
  proof?: number
  category: string
  description: string
  images: string[]
  location: string
  tradePreferences: string
  status: 'available' | 'pending' | 'completed' | 'expired'
  createdAt: string
  expiresAt: string
}

export interface CreateBourbonListingData {
  type: 'have' | 'want'
  title: string
  brand?: string
  age?: string | number // Support both string (e.g. "NAS") and number (e.g. 12)
  proof?: number
  category: string
  description?: string
  images?: string[]
  location?: string
  tradePreferences?: string
}

export interface UpdateBourbonListingData {
  type?: 'have' | 'want'
  title?: string
  brand?: string
  age?: string | number // Support both string (e.g. "NAS") and number (e.g. 12)
  proof?: number
  category?: string
  description?: string
  images?: string[]
  location?: string
  tradePreferences?: string
  status?: 'available' | 'pending' | 'completed' | 'expired'
}

export interface BourbonResponse {
  items: BourbonListing[]
  count: number
  hasMore: boolean
  nextKey?: string
}

export interface BourbonSearchParams {
  limit?: number
  lastKey?: string
  type?: 'have' | 'want'
  category?: string
  brand?: string
  location?: string
  search?: string
  status?: string
}

export const bourbonApi = {
  // Get bourbon listings with pagination and search
  async getListings(
    getAccessTokenSilently: () => Promise<string>, 
    options?: BourbonSearchParams
  ): Promise<BourbonResponse> {
    const token = await getAccessTokenSilently()
    
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.lastKey) params.append('lastKey', options.lastKey)
    if (options?.type) params.append('type', options.type)
    if (options?.category) params.append('category', options.category)
    if (options?.brand) params.append('brand', options.brand)
    if (options?.location) params.append('location', options.location)
    if (options?.search) params.append('search', options.search)
    if (options?.status) params.append('status', options.status)
    
    const url = `${API_BASE_URL}/v1/bourbon${params.toString() ? `?${params}` : ''}`
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch bourbon listings: ${response.status}`)
    }

    return response.json()
  },

  // Get a single bourbon listing
  async getListing(id: string, getAccessTokenSilently: () => Promise<string>): Promise<BourbonListing> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/bourbon/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch bourbon listing: ${response.status}`)
    }

    return response.json()
  },

  // Create a new bourbon listing
  async createListing(data: CreateBourbonListingData, getAccessTokenSilently: () => Promise<string>): Promise<{ id: string, message: string }> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/bourbon`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || `Failed to create bourbon listing: ${response.status}`)
    }

    return response.json()
  },

  // Update a bourbon listing
  async updateListing(id: string, data: UpdateBourbonListingData, getAccessTokenSilently: () => Promise<string>): Promise<{ message: string }> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/bourbon/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || `Failed to update bourbon listing: ${response.status}`)
    }

    return response.json()
  },

  // Delete a bourbon listing
  async deleteListing(id: string, getAccessTokenSilently: () => Promise<string>): Promise<{ message: string }> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/bourbon/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || `Failed to delete bourbon listing: ${response.status}`)
    }

    return response.json()
  }
}
